import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";

import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";

import planilhaExemplo from "../../assets/planilha.xlsx";
import ForbiddenPage from "../../components/ForbiddenPage";
import MetricsStrip from "../../components/MetricsStrip";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((item) => item.id === contact.id);

      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((item) => item.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    }

    return [contact, ...state];
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    return state.filter((item) => item.id !== contactId);
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  statusCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  contactName: {
    fontWeight: 600,
  },
  helperText: {
    opacity: 0.72,
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
  },
  loadMoreBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  hiddenInput: {
    display: "none",
  },
  validIcon: {
    color: "green",
  },
  invalidIcon: {
    color: "grey",
  },
}));

const ContactListItems = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const { contactListId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);

  const { findById: findContactList } = useContactLists();

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
  }, [contactListId, findContactList]);

  const fetchContacts = useCallback(
    async (currentSearch = "", currentPage = 1) => {
      try {
        const { data } = await api.get("contact-list-items", {
          params: {
            searchParam: currentSearch,
            pageNumber: currentPage,
            contactListId,
          },
        });

        dispatch({ type: "LOAD_CONTACTS", payload: data.contacts || [] });
        setHasMore(Boolean(data.hasMore));
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [contactListId]
  );

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      fetchContacts(searchParam, pageNumber);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchContacts, pageNumber, searchParam]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCompanyContactLists = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        dispatch({ type: "LOAD_CONTACTS", payload: data.records });
      }
    };

    socket.on(`company-${companyId}-ContactListItem`, onCompanyContactLists);

    return () => {
      socket.off(`company-${companyId}-ContactListItem`, onCompanyContactLists);
    };
  }, [contactListId, socket, user.companyId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const handleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingContact(null);
      setConfirmOpen(false);
    }
  };

  const handleImportContacts = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);

      await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
      });
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmOpen(false);
    }
  };

  const refreshList = async () => {
    dispatch({ type: "RESET" });
    setLoading(true);

    if (pageNumber !== 1) {
      setPageNumber(1);
      return;
    }

    await fetchContacts(searchParam, 1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    history.push("/contact-lists");
  };

  const validWhatsappCount = useMemo(
    () => contacts.filter((contact) => Boolean(contact.isWhatsappValid)).length,
    [contacts]
  );

  const withEmailCount = useMemo(
    () => contacts.filter((contact) => Boolean(String(contact.email || "").trim())).length,
    [contacts]
  );

  const summaryCards = useMemo(
    () => [
      { key: "contacts", label: "Contatos", value: contacts.length },
      { key: "whatsapp", label: "WhatsApp valido", value: validWhatsappCount },
      { key: "email", label: "Com email", value: withEmailCount },
      { key: "pages", label: "Mais paginas", value: hasMore ? "Sim" : "Nao" },
    ],
    [contacts.length, hasMore, validWhatsappCount, withEmailCount]
  );

  if (user.profile === "user") {
    return (
      <MainContainer className={classes.mainContainer} fullWidth>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      />

      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeletingContact(null);
        }}
        onConfirm={() =>
          deletingContact ? handleDeleteContact(deletingContact.id) : handleImportContacts()
        }
      >
        {deletingContact ? (
          `${i18n.t("contactListItems.confirmationModal.deleteMessage")}`
        ) : (
          <>
            {i18n.t("contactListItems.confirmationModal.importMessage")}{" "}
            <a href={planilhaExemplo} download="planilha.xlsx">
              Clique aqui para baixar planilha exemplo.
            </a>
          </>
        )}
      </ConfirmationModal>

      <input
        className={classes.hiddenInput}
        id="upload"
        name="file"
        type="file"
        accept=".xls,.xlsx"
        onChange={() => {
          setDeletingContact(null);
          setConfirmOpen(true);
        }}
        ref={fileUploadRef}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {contactList.name || "Contatos da lista"}
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button variant="outlined" color="primary" onClick={refreshList} disabled={loading}>
              Atualizar
            </Button>
            <Button variant="outlined" color="primary" onClick={goToContactLists}>
              {i18n.t("contactListItems.buttons.lists")}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                if (fileUploadRef.current) {
                  fileUploadRef.current.value = null;
                  fileUploadRef.current.click();
                }
              }}
            >
              {i18n.t("contactListItems.buttons.import")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenContactListItemModal}
            >
              {i18n.t("contactListItems.buttons.add")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores dos contatos da lista" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Contatos da lista
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("contactListItems.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar contatos da lista" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${contacts.length} visiveis`} />
              <Chip size="small" label={`${validWhatsappCount} validos`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    Status
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    {i18n.t("contactListItems.table.name")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("contactListItems.table.number")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("contactListItems.table.email")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("contactListItems.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id} hover>
                    <TableCell align="center" className={classes.statusCell}>
                      <IconButton size="small">
                        {contact.isWhatsappValid ? (
                          <CheckCircleIcon
                            titleAccess="Whatsapp valido"
                            className={classes.validIcon}
                          />
                        ) : (
                          <BlockIcon
                            titleAccess="Whatsapp invalido"
                            className={classes.invalidIcon}
                          />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.contactName}>{contact.name}</Typography>
                    </TableCell>
                    <TableCell align="center">{contact.number}</TableCell>
                    <TableCell align="center">{contact.email || "-"}</TableCell>
                    <TableCell align="center" className={classes.actionsCell}>
                      <IconButton size="small" onClick={() => handleEditContact(contact.id)}>
                        <EditIcon />
                      </IconButton>

                      <Can
                        role={user.profile}
                        perform="contacts-page:deleteContact"
                        yes={() => (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeletingContact(contact);
                              setConfirmOpen(true);
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                ))}

                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && contacts.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6">Nenhum contato encontrado</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou importe novos contatos para continuar.
              </Typography>
            </Box>
          ) : null}

          {!loading && hasMore && contacts.length > 0 ? (
            <div className={classes.loadMoreBox}>
              <Button variant="outlined" color="primary" onClick={loadMore}>
                Carregar mais contatos
              </Button>
            </div>
          ) : null}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default ContactListItems;
